import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useAppSelector } from '@/store/hooks';
import { selectActiveLocationDetails } from '@/store/slices/activeLocationSlice';
import { query, where, onSnapshot, collectionGroup, getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { EditorInteractable } from '@/components/editor/EditorInteractable';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from "@/components/ui/carousel";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface PublicComment {
  userId: string;
  username: string;
  publicNote: string;
}

export const LocationComments = () => {
  const location = useAppSelector(selectActiveLocationDetails);
  const [publicComments, setPublicComments] = useState<PublicComment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(true);

  useEffect(() => {
    if (!location?.id) {
      setPublicComments([]);
      setIsLoadingComments(false);
      return;
    }

    setIsLoadingComments(true);
    const usernames: Record<string, string> = {};

    const fetchUsernamesAndListen = async () => {
      try {
        const visitedLocationsQuery = query(
          collectionGroup(db, 'visitedLocations'),
          where("locationId", "==", location.id),
          where("publicNote", ">", "")
        );

        const unsubscribe = onSnapshot(visitedLocationsQuery, async (querySnapshot) => {
          const allComments: PublicComment[] = [];
          const usernamesToFetch = new Set<string>();
          
          // First pass: collect all unique user IDs and create comment objects
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const userId = doc.ref.parent.parent?.id;
            if (userId && data.publicNote && data.publicNote.trim() !== '') {
              usernamesToFetch.add(userId);
              allComments.push({
                userId: userId,
                username: usernames[userId] || 'Anonymous', // Use cached if available
                publicNote: data.publicNote
              });
            }
          });
          
          // Second pass: fetch any missing usernames individually
          const fetchPromises = Array.from(usernamesToFetch)
            .filter(userId => !usernames[userId]) // Only fetch if not already cached
            .map(async (userId) => {
              try {
                const userDoc = await getDoc(doc(db, 'users', userId));
                if (userDoc.exists()) {
                  usernames[userId] = userDoc.data().username || 'Anonymous';
                } else {
                  usernames[userId] = 'Anonymous';
                }
              } catch (error) {
                console.warn(`Failed to fetch username for user ${userId}:`, error);
                usernames[userId] = 'Anonymous';
              }
            });
          
          if (fetchPromises.length > 0) {
            await Promise.all(fetchPromises);
            
            // Update comment objects with newly fetched usernames
            allComments.forEach(comment => {
              comment.username = usernames[comment.userId] || 'Anonymous';
            });
          }
          
          allComments.sort((a, b) => a.username.localeCompare(b.username));
          setPublicComments(allComments);
          setIsLoadingComments(false);
        }, (error) => {
          console.error('Error listening to public comments:', error);
          setPublicComments([]);
          setIsLoadingComments(false);
        });

        return unsubscribe;

      } catch (error) {
        console.error('Error fetching usernames or setting up listener:', error);
        setPublicComments([]);
        setIsLoadingComments(false);
        return () => {};
      }
    };

    const unsubscribePromise = fetchUsernamesAndListen();

    return () => {
      unsubscribePromise.then(unsubscribe => unsubscribe && unsubscribe());
    };
  }, [location?.id]);

  return (
    <div className="flex items-center py-1 min-h-[40px]">
      {isLoadingComments ? (
        <div className="flex items-center justify-center w-full h-10">
          <Loader2 className="h-4 w-4 animate-spin mr-2 text-muted-foreground" />
          <span className="text-xs text-muted-foreground italic">Loading comments...</span>
        </div>
      ) : publicComments.length > 0 ? (
        <Carousel className="w-full" opts={{ loop: publicComments.length > 1, align: "center" }}>
          <CarouselContent className="ml-0 h-10"> 
            {publicComments.map((comment, index) => (
              <CarouselItem key={`${comment.userId}-${index}`} className="pl-0 basis-full flex items-center justify-center">
                <div className="flex-1 min-w-0 px-10 text-center h-full flex items-center">
                  {location?.id && (
                    <PermissionGuard requiredPermission="editor">
                      {(hasPermission: boolean) => (
                        hasPermission ? (
                          <EditorInteractable
                            entityType="visitedLocationNote"
                            entityId={location.id!}
                            authorUserId={comment.userId}
                            className="h-full w-full flex items-center justify-center"
                          >
                            <Tooltip>
                              <TooltipTrigger asChild className="h-full w-full">
                                <div className="relative pb-0 cursor-default h-full w-full flex flex-col justify-center items-center text-center">
                                  <p className="text-xs mb-0 line-clamp-2 overflow-hidden text-ellipsis px-1">
                                    "{comment.publicNote}"
                                  </p>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[250px]">
                                <div>
                                  <p className="mb-1">{comment.publicNote}</p>
                                  <p className="text-right text-xs italic">– {comment.username}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </EditorInteractable>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild className="h-full w-full">
                              <div className="relative pb-0 cursor-default h-full w-full flex flex-col justify-center items-center text-center">
                                <p className="text-xs mb-0 line-clamp-2 overflow-hidden text-ellipsis px-1">
                                  {comment.publicNote}
                                </p>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[250px]">
                              <div>
                                <p className="mb-1">{comment.publicNote}</p>
                                <p className="text-right text-xs italic">– {comment.username}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )
                      )}
                    </PermissionGuard>
                  )}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          {publicComments.length > 1 && (
            <>
              <CarouselPrevious 
                className="h-7 w-7 left-0 translate-x-0 transform-none z-10 text-muted-foreground hover:text-foreground"
                variant="ghost"
              />
              <CarouselNext 
                className="h-7 w-7 right-0 translate-x-0 transform-none z-10 text-muted-foreground hover:text-foreground"
                variant="ghost"
              />
            </>
          )}
        </Carousel>
      ) : (
        <p className="text-xs text-muted-foreground italic ml-2 w-full text-center">No public comments yet</p>
      )}
    </div>
  );
}; 